"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { fetchAllStaff } from "@/lib/api";
import type { StaffMember } from "@/types/page";
import type { SiteTemplateKey } from "@/lib/siteTemplates";
import { getTemplateStyles } from "@/lib/siteTemplateStyles";

export function TeamPreviewBlock({
  content,
  template = "classic",
}: {
  content: Record<string, unknown>;
  template?: SiteTemplateKey;
}) {
  const heading = String(content.heading ?? "");
  const intro = String(content.intro ?? "");
  const limit = Number(content.limit ?? 6);
  const [members, setMembers] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const styles = getTemplateStyles(template);

  useEffect(() => {
    fetchAllStaff()
      .then((all) => {
        const visible = Number.isFinite(limit) && limit > 0 ? all.slice(0, limit) : all;
        setMembers(visible);
      })
      .catch(() => setMembers([]))
      .finally(() => setLoading(false));
  }, [limit]);

  if (!heading && !intro && members.length === 0 && !loading) return null;

  return (
    <section className={styles.textGroup.section}>
      {(heading || intro) && (
        <div className="mb-[calc(var(--space-unit)*1.5)] max-w-3xl">
          {heading && (
            <h2
              className="font-bold text-[var(--color-text)]"
              style={{ fontFamily: "var(--font-heading)", fontSize: "var(--text-h2)", lineHeight: "var(--line-heading)" }}
            >
              {heading}
            </h2>
          )}
          {intro && <p className="mt-2 text-[var(--color-secondary)]">{intro}</p>}
        </div>
      )}
      {members.length === 0 && !loading ? (
        <p className="text-sm text-[var(--color-secondary)]">No team members published yet.</p>
      ) : (
        <ul className="grid grid-cols-1 gap-[calc(var(--space-unit)*1.5)] sm:grid-cols-2 lg:grid-cols-3">
          {members.map((member) => (
            <li key={member.id} className={styles.textGroup.card}>
              {member.photoUrl && (
                <div className="relative mb-3 aspect-square w-full overflow-hidden rounded-[var(--border-radius)]">
                  <Image
                    src={member.photoUrl}
                    alt={member.name}
                    fill
                    className="object-cover"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 360px"
                  />
                </div>
              )}
              <h3 className="font-semibold text-[var(--color-text)]" style={{ fontFamily: "var(--font-heading)" }}>
                {member.name}
              </h3>
              {member.title && <p className="text-sm text-[var(--color-primary)]">{member.title}</p>}
              {member.bio && <p className="mt-2 text-sm text-[var(--color-secondary)]">{member.bio}</p>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
